import { motion } from 'framer-motion';

export const AnimatedLogo = ({ size = 'default', showText = true, className = '' }) => {
    const sizes = {
        small: { icon: 40, text: 'text-lg', subtext: 'text-xs' },
        default: { icon: 48, text: 'text-xl', subtext: 'text-xs' },
        large: { icon: 80, text: 'text-3xl', subtext: 'text-sm' },
        hero: { icon: 120, text: 'text-5xl', subtext: 'text-lg' },
    };

    const currentSize = sizes[size] || sizes.default;

    // Sound wave bars animation
    const bars = [
        { height: 12, delay: 0 },
        { height: 20, delay: 0.1 },
        { height: 28, delay: 0.2 },
        { height: 16, delay: 0.3 },
        { height: 24, delay: 0.15 },
    ];

    return (
        <div className={`flex items-center gap-3 ${className}`}>
            {/* Animated Logo Icon */}
            <motion.div
                className="relative"
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300 }}
            >
                {/* Outer Glow Ring */}
                <motion.div
                    className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary via-accent to-primary opacity-50 blur-lg"
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.6, 0.3],
                    }}
                    transition={{
                        duration: 3,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    style={{ width: currentSize.icon, height: currentSize.icon }}
                />

                {/* Main Logo Container */}
                <motion.div
                    className="relative rounded-2xl bg-gradient-to-br from-primary via-accent to-primary p-0.5 overflow-hidden"
                    style={{ width: currentSize.icon, height: currentSize.icon }}
                >
                    {/* Inner Dark Background */}
                    <div className="w-full h-full rounded-xl bg-background/90 backdrop-blur-sm flex items-center justify-center overflow-hidden">
                        {/* Sound Wave Bars */}
                        <div className="flex items-end gap-0.5 h-full py-2">
                            {bars.map((bar, index) => (
                                <motion.div
                                    key={index}
                                    className="w-1 sm:w-1.5 rounded-full bg-gradient-to-t from-primary to-accent"
                                    animate={{
                                        height: [bar.height * 0.4, bar.height, bar.height * 0.4],
                                    }}
                                    transition={{
                                        duration: 0.8,
                                        repeat: Infinity,
                                        delay: bar.delay,
                                        ease: 'easeInOut',
                                    }}
                                />
                            ))}
                        </div>

                        {/* Floating Music Notes */}
                        <motion.div
                            className="absolute text-primary/30 text-xs"
                            style={{ top: '20%', right: '15%' }}
                            animate={{
                                y: [-5, -15, -5],
                                opacity: [0.3, 0.7, 0.3],
                                rotate: [-10, 10, -10],
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut',
                            }}
                        >
                            ♪
                        </motion.div>
                        <motion.div
                            className="absolute text-accent/30 text-xs"
                            style={{ bottom: '25%', left: '10%' }}
                            animate={{
                                y: [0, -10, 0],
                                opacity: [0.2, 0.5, 0.2],
                                rotate: [10, -10, 10],
                            }}
                            transition={{
                                duration: 2.5,
                                repeat: Infinity,
                                ease: 'easeInOut',
                                delay: 0.5,
                            }}
                        >
                            ♫
                        </motion.div>
                    </div>
                </motion.div>
            </motion.div>

            {/* Logo Text */}
            {showText && (
                <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex flex-col"
                >
                    <span className={`font-heading font-bold ${currentSize.text} text-foreground leading-tight`}>
                        Hawayein
                    </span>
                    <span className={`${currentSize.subtext} text-muted-foreground font-body tracking-wider`}>
                        THE BAND
                    </span>
                </motion.div>
            )}
        </div>
    );
};

// Hero Logo - Larger animated version for hero section
export const HeroLogo = ({ className = '' }) => {
    const bars = [
        { height: 30, delay: 0 },
        { height: 50, delay: 0.1 },
        { height: 70, delay: 0.2 },
        { height: 40, delay: 0.3 },
        { height: 60, delay: 0.15 },
        { height: 45, delay: 0.25 },
        { height: 55, delay: 0.05 },
    ];

    return (
        <div className={`flex flex-col items-center gap-6 ${className}`}>
            {/* Large Animated Icon */}
            <motion.div
                className="relative"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', stiffness: 100, delay: 0.2 }}
            >
                {/* Multiple Glow Rings */}
                <motion.div
                    className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary to-accent opacity-30 blur-2xl"
                    animate={{
                        scale: [1, 1.3, 1],
                        opacity: [0.2, 0.4, 0.2],
                    }}
                    transition={{
                        duration: 4,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    style={{ width: 140, height: 140 }}
                />
                <motion.div
                    className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-accent to-amber opacity-20 blur-3xl"
                    animate={{
                        scale: [1.1, 1.4, 1.1],
                        opacity: [0.1, 0.3, 0.1],
                    }}
                    transition={{
                        duration: 5,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: 0.5,
                    }}
                    style={{ width: 140, height: 140 }}
                />

                {/* Main Icon */}
                <motion.div
                    className="relative w-32 h-32 sm:w-36 sm:h-36 rounded-3xl bg-gradient-to-br from-primary via-accent to-primary p-1 overflow-hidden"
                    whileHover={{ rotate: [0, -5, 5, 0] }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="w-full h-full rounded-2xl bg-background/95 backdrop-blur-md flex items-center justify-center">
                        {/* Sound Wave Bars */}
                        <div className="flex items-end gap-1 h-full py-4">
                            {bars.map((bar, index) => (
                                <motion.div
                                    key={index}
                                    className="w-2 sm:w-2.5 rounded-full bg-gradient-to-t from-primary via-accent to-primary"
                                    animate={{
                                        height: [bar.height * 0.3, bar.height, bar.height * 0.3],
                                        opacity: [0.7, 1, 0.7],
                                    }}
                                    transition={{
                                        duration: 0.7 + index * 0.1,
                                        repeat: Infinity,
                                        delay: bar.delay,
                                        ease: 'easeInOut',
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </motion.div>
            </motion.div>

            {/* Large Text */}
            <motion.div
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.7 }}
            >
                <h1 className="font-heading font-black text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-foreground mb-2 tracking-tight">
                    <motion.span
                        className="inline-block"
                        animate={{
                            textShadow: [
                                '0 0 20px hsl(210 100% 55% / 0.3)',
                                '0 0 40px hsl(210 100% 55% / 0.5)',
                                '0 0 20px hsl(210 100% 55% / 0.3)',
                            ],
                        }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        Hawayein
                    </motion.span>
                </h1>
                <motion.p
                    className="font-heading font-semibold text-xl sm:text-2xl md:text-3xl text-gradient-primary tracking-[0.3em]"
                    initial={{ opacity: 0, letterSpacing: '0.5em' }}
                    animate={{ opacity: 1, letterSpacing: '0.3em' }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                >
                    THE BAND
                </motion.p>
            </motion.div>
        </div>
    );
};
